import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useTimezone } from '../contexts/TimezoneContext';
import { Badge } from '../components/ui/badge';
import { TrendingUp, TrendingDown, Clock, MapPin, Heart, CheckCircle2, XCircle, Radio, Target, AlertTriangle, ThumbsUp, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { ConfidenceBreakdownButton } from './ConfidenceBreakdownButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GameCard = ({ game, className, onPredictionUpdate }) => {
  const { isAuthenticated, isSubscriber, favoriteTeams, toggleFavoriteTeam, user } = useAuth();
  const { formatGameTime, formatLocalTime, timezoneAbbr } = useTimezone();
  const navigate = useNavigate();
  const [userPrediction, setUserPrediction] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);
  const [isPredicting, setIsPredicting] = useState(false);
  const [loadingPrediction, setLoadingPrediction] = useState(false);
  
  // Helper to format time in user's timezone
  const formatMSTTime = (isoString) => {
    const time = formatGameTime(isoString);
    return time ? `${time} ${timezoneAbbr}` : '';
  };

  // Helper to format date in user's timezone
  const formatMSTDate = (isoString) => {
    return formatLocalTime(isoString, { weekday: 'short', month: 'short', day: 'numeric' });
  };
  
  const homeWinning = game.home_probability > game.away_probability;
  const isFeatured = game.is_featured;
  const isLive = game.status === 'live';
  const isFinal = game.status === 'final';
  const isScheduled = game.status === 'scheduled';
  const hasScorePrediction = game.ai_home_score !== null && game.ai_away_score !== null;
  const isHomeFavorite = favoriteTeams.includes(game.home_team?.team_id);
  const isAwayFavorite = favoriteTeams.includes(game.away_team?.team_id);
  const canPredict = isSubscriber && isScheduled;

  // Fetch community prediction stats for ALL users (platform-wide)
  // And user's own prediction if authenticated
  useEffect(() => {
    const fetchPredictionData = async () => {
      setLoadingPrediction(true);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Always fetch community stats (visible to all)
        const response = await fetch(`${API_URL}/api/predictions/game/${game.game_id}`, { headers });
        if (response.ok) {
          const data = await response.json();
          setUserPrediction(data.user_prediction);
          setCommunityStats(data.community_stats);
        }
      } catch (err) {
        console.error('Failed to fetch prediction data:', err);
      } finally {
        setLoadingPrediction(false);
      }
    };
    fetchPredictionData();
  }, [game.game_id]);

  // Handle quick prediction
  const handleQuickPredict = async (e, predictedWinner) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isSubscriber) {
      toast.error('Predictions are available for subscribers only');
      navigate('/billing');
      return;
    }
    
    if (!isScheduled) {
      toast.error('Game has already started - predictions locked');
      return;
    }
    
    setIsPredicting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/predictions/quick?game_id=${game.game_id}&predicted_winner=${predictedWinner}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserPrediction({
          predicted_winner: predictedWinner,
          ...data
        });
        toast.success(data.is_update ? 'Prediction updated!' : 'Prediction recorded!');
        onPredictionUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save prediction');
      }
    } catch (err) {
      toast.error('Failed to save prediction');
    } finally {
      setIsPredicting(false);
    }
  };

  // For live/final games, determine who's actually winning by score
  const homeScoreWinning = isLive || isFinal ? (game.home_score || 0) > (game.away_score || 0) : false;
  const awayScoreWinning = isLive || isFinal ? (game.away_score || 0) > (game.home_score || 0) : false;
  
  // Determine predicted winner matching backend logic (probability + score tiebreaker)
  const getPredictedWinner = () => {
    if (!game.home_probability && !game.away_probability) return null;
    
    const probDiff = Math.abs(game.home_probability - game.away_probability);
    
    // When probability difference is >= 1%, use probability
    if (probDiff >= 1.0) {
      return game.home_probability > game.away_probability ? 'home' : 'away';
    }
    
    // For very close probabilities (< 1% diff), use score prediction as tiebreaker
    const homeScore = game.ai_home_score ?? game.platform_home_score;
    const awayScore = game.ai_away_score ?? game.platform_away_score;
    
    if (homeScore != null && awayScore != null) {
      if (homeScore > awayScore) return 'home';
      if (awayScore > homeScore) return 'away';
    }
    
    // Fallback to probability
    return game.home_probability >= game.away_probability ? 'home' : 'away';
  };
  
  const predictedWinner = getPredictedWinner();
  
  // Calculate actual winner - handle draws in soccer
  const getActualWinner = () => {
    if (!isFinal) return null;
    const homeScore = game.home_score;
    const awayScore = game.away_score;
    
    if (homeScore === null || awayScore === null || homeScore === undefined || awayScore === undefined) {
      return null;
    }
    
    if (homeScore > awayScore) return 'home';
    if (awayScore > homeScore) return 'away';
    // Draw - return 'draw' for soccer leagues
    return 'draw';
  };
  
  const actualWinner = getActualWinner();
  
  // Calculate prediction correctness
  // Use backend's prediction_correct if available, otherwise calculate
  // For draws: prediction is incorrect unless we somehow predicted a draw
  const calculatePredictionCorrect = () => {
    if (!isFinal) return null;
    
    // Prefer backend value if it exists
    if (game.prediction_correct !== null && game.prediction_correct !== undefined) {
      return game.prediction_correct;
    }
    
    // Calculate locally if backend didn't provide
    if (!predictedWinner || !actualWinner) return null;
    
    // If it's a draw, the prediction is incorrect (unless both probs were exactly 50%)
    if (actualWinner === 'draw') {
      return false;
    }
    
    return predictedWinner === actualWinner;
  };
  
  const predictionCorrect = calculatePredictionCorrect();

  // Navigate to game detail with prediction tab
  const handlePredictionClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/game/${game.game_id}?tab=prediction`, { 
      state: { fromBoard: boardState } 
    });
  };

  const getConfidenceClass = (confidence) => {
    switch (confidence?.toLowerCase()) {
      case 'high': return 'confidence-high';
      case 'medium': return 'confidence-medium';
      case 'low': return 'confidence-low';
      default: return 'confidence-medium';
    }
  };

  const handleFavoriteClick = (e, teamId) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAuthenticated && toggleFavoriteTeam) {
      toggleFavoriteTeam(teamId);
    }
  };

  // Create navigation state to return to the same board position
  const boardState = {
    gameId: game.game_id,
    league: game.league_id,
    date: game.date || game.game_date_mst?.split('T')[0]
  };

  return (
    <Link 
      to={`/game/${game.game_id}`}
      state={{ fromBoard: boardState }}
      className={cn(
        "game-card block bg-card border border-border rounded-sm p-4 relative overflow-hidden",
        isFeatured && "border-red-500/30 col-span-1 md:col-span-2",
        isLive && "border-red-500/50 animate-pulse-subtle",
        className
      )}
      data-testid={`game-card-${game.game_id}`}
    >
      {/* Featured badge */}
      {isFeatured && !isLive && !isFinal && (
        <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold uppercase px-2 py-1 tracking-wider">
          Featured
        </div>
      )}

      {/* Result badge for completed games - show if we have probability-based prediction */}
      {isFinal && predictionCorrect !== null && predictedWinner !== null && (
        <div className={`absolute top-0 right-0 ${predictionCorrect ? 'bg-green-500' : 'bg-red-500'} text-white text-xs font-bold uppercase px-2 py-1 tracking-wider flex items-center gap-1`}>
          {predictionCorrect ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
          {predictionCorrect ? 'Correct' : 'Incorrect'}
        </div>
      )}

      {/* Live badge with period/clock */}
      {isLive && (
        <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold uppercase px-2 py-1 tracking-wider live-badge flex items-center gap-1">
          <Radio className="w-3 h-3 animate-pulse" />
          <span>LIVE</span>
          {game.status_detail && (
            <span className="font-normal ml-1">{game.status_detail}</span>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono text-xs uppercase">
            {game.league_name}
          </Badge>
          {!isLive && !isFinal && (
            <Badge className={cn("text-xs uppercase", getConfidenceClass(game.confidence))}>
              {game.confidence}
            </Badge>
          )}
          {!isFinal && (
            <ConfidenceBreakdownButton gameId={game.game_id} leagueId={game.league_id} compact />
          )}
          {isFinal && (
            <Badge variant="outline" className="text-xs uppercase">Final</Badge>
          )}
        </div>
      </div>

      {/* Teams */}
      <div className="space-y-3">
        {/* Away Team */}
        <div className={cn(
          "flex items-center justify-between p-2 rounded-sm",
          !homeWinning && !isLive && !isFinal && "bg-secondary/50",
          (isLive || isFinal) && awayScoreWinning && "bg-green-500/10"
        )}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary rounded-sm flex items-center justify-center overflow-hidden">
              {game.away_team?.logo_url ? (
                <img 
                  src={game.away_team.logo_url} 
                  alt={game.away_team.name}
                  className="w-8 h-8 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <span className="font-display font-bold text-sm">
                  {game.away_team?.abbreviation}
                </span>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm">{game.away_team?.name}</p>
                {isAuthenticated && (
                  <button
                    onClick={(e) => handleFavoriteClick(e, game.away_team?.team_id)}
                    className="p-0.5 hover:bg-secondary/50 rounded-sm transition-colors"
                  >
                    <Heart className={`w-3 h-3 ${isAwayFavorite ? 'text-red-400 fill-red-400' : 'text-muted-foreground'}`} />
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Away</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Live/Final Score */}
            {(isLive || isFinal) && game.away_score !== null && game.away_score !== undefined && (
              <span className={cn(
                "font-mono font-bold text-2xl",
                awayScoreWinning ? "text-green-400" : "text-foreground"
              )}>
                {game.away_score}
              </span>
            )}
            {/* Probability */}
            <div className="flex items-center gap-1">
              {!homeWinning && !isLive && !isFinal && <TrendingUp className="w-4 h-4 text-green-400" />}
              <span className={cn(
                "font-mono font-semibold",
                isLive || isFinal ? "text-sm text-muted-foreground" : "text-lg",
                !homeWinning && !isLive && !isFinal ? "text-green-400" : "text-muted-foreground"
              )}>
                {game.away_probability.toFixed(0)}%
              </span>
            </div>
          </div>
        </div>

        {/* Home Team */}
        <div className={cn(
          "flex items-center justify-between p-2 rounded-sm",
          homeWinning && !isLive && !isFinal && "bg-secondary/50",
          (isLive || isFinal) && homeScoreWinning && "bg-green-500/10"
        )}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary rounded-sm flex items-center justify-center overflow-hidden">
              {game.home_team?.logo_url ? (
                <img 
                  src={game.home_team.logo_url} 
                  alt={game.home_team.name}
                  className="w-8 h-8 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <span className="font-display font-bold text-sm">
                  {game.home_team?.abbreviation}
                </span>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm">{game.home_team?.name}</p>
                {isAuthenticated && (
                  <button
                    onClick={(e) => handleFavoriteClick(e, game.home_team?.team_id)}
                    className="p-0.5 hover:bg-secondary/50 rounded-sm transition-colors"
                  >
                    <Heart className={`w-3 h-3 ${isHomeFavorite ? 'text-red-400 fill-red-400' : 'text-muted-foreground'}`} />
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Home</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Live/Final Score */}
            {(isLive || isFinal) && game.home_score !== null && game.home_score !== undefined && (
              <span className={cn(
                "font-mono font-bold text-2xl",
                homeScoreWinning ? "text-green-400" : "text-foreground"
              )}>
                {game.home_score}
              </span>
            )}
            {/* Probability */}
            <div className="flex items-center gap-1">
              {homeWinning && !isLive && !isFinal && <TrendingUp className="w-4 h-4 text-green-400" />}
              <span className={cn(
                "font-mono font-semibold",
                isLive || isFinal ? "text-sm text-muted-foreground" : "text-lg",
                homeWinning && !isLive && !isFinal ? "text-green-400" : "text-muted-foreground"
              )}>
                {game.home_probability.toFixed(0)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Score Prediction - Always show for scheduled and live games */}
      {(isScheduled || isLive) && hasScorePrediction && (
        <div 
          onClick={handlePredictionClick}
          className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-sm cursor-pointer hover:bg-emerald-500/20 transition-colors"
          data-testid="score-prediction-section"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-medium text-emerald-400 uppercase tracking-wide">
                Score Prediction
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <span className="font-mono text-lg font-bold text-foreground">{game.ai_away_score}</span>
                <span className="text-xs text-muted-foreground ml-1">{game.away_team?.abbreviation}</span>
              </div>
              <span className="text-muted-foreground">-</span>
              <div className="text-center">
                <span className="font-mono text-lg font-bold text-foreground">{game.ai_home_score}</span>
                <span className="text-xs text-muted-foreground ml-1">{game.home_team?.abbreviation}</span>
              </div>
            </div>
          </div>
          {game.ai_analysis_summary && (
            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{game.ai_analysis_summary}</p>
          )}
        </div>
      )}

      {/* Prediction vs Actual Comparison - Show for final games with predictions */}
      {isFinal && predictedWinner && predictionCorrect !== null && (
        <div className={cn(
          "mt-3 p-3 rounded-sm border",
          predictionCorrect ? "bg-green-500/10 border-green-500/30" : "bg-orange-500/10 border-orange-500/30"
        )}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {predictionCorrect ? (
                <CheckCircle2 className="w-4 h-4 text-green-400" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-orange-400" />
              )}
              <span className={cn(
                "text-xs font-medium uppercase tracking-wide",
                predictionCorrect ? "text-green-400" : "text-orange-400"
              )}>
                {predictionCorrect ? "Prediction Correct" : "Prediction Missed"}
              </span>
            </div>
          </div>
          
          {/* Comparison Grid - only show if we have score predictions */}
          {hasScorePrediction && (
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div></div>
              <div className="text-muted-foreground">Predicted</div>
              <div className="text-muted-foreground">Actual</div>
              
              <div className="text-left font-medium">{game.away_team?.abbreviation}</div>
              <div className="font-mono">{game.ai_away_score}</div>
              <div className={cn("font-mono font-bold", awayScoreWinning && "text-green-400")}>{game.away_score}</div>
              
              <div className="text-left font-medium">{game.home_team?.abbreviation}</div>
              <div className="font-mono">{game.ai_home_score}</div>
              <div className={cn("font-mono font-bold", homeScoreWinning && "text-green-400")}>{game.home_score}</div>
            </div>
          )}
          
          {/* Win probability comparison */}
          <div className={cn("flex justify-between text-xs text-muted-foreground", hasScorePrediction && "mt-2 pt-2 border-t border-border/50")}>
            <span>Predicted: {predictedWinner === 'home' ? game.home_team?.abbreviation : game.away_team?.abbreviation} ({game[predictedWinner === 'home' ? 'home_probability' : 'away_probability']?.toFixed(0)}%)</span>
            <span>Actual: {actualWinner === 'home' ? game.home_team?.abbreviation : game.away_team?.abbreviation}</span>
          </div>
        </div>
      )}

      {/* Subscribers' Predictions Section - Shows community stats to ALL users (only if 20+ predictions) */}
      {isScheduled && communityStats && communityStats.total_predictions >= 20 && (
        <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/20 rounded-sm" data-testid="community-prediction-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ThumbsUp className="w-4 h-4 text-purple-400" />
              <span className="text-xs font-medium text-purple-400 uppercase tracking-wide">
                Subscribers' Picks
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              {communityStats.total_predictions} prediction{communityStats.total_predictions !== 1 ? 's' : ''}
            </span>
          </div>
          
          {/* Platform vs Subscribers comparison */}
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div className="text-center p-2 bg-secondary/30 rounded-sm">
              <p className="text-xs text-muted-foreground mb-1">Platform</p>
              <p className="font-bold text-sm">
                {game.home_probability > game.away_probability 
                  ? `${game.home_team?.abbreviation} ${game.home_probability.toFixed(0)}%`
                  : game.away_probability > game.home_probability
                    ? `${game.away_team?.abbreviation} ${game.away_probability.toFixed(0)}%`
                    : 'Toss-up 50%'}
              </p>
            </div>
            <div className="text-center p-2 bg-secondary/30 rounded-sm">
              <p className="text-xs text-muted-foreground mb-1">Subscribers</p>
              <p className="font-bold text-sm">
                {communityStats.home_percentage > communityStats.away_percentage 
                  ? `${game.home_team?.abbreviation} ${communityStats.home_percentage}%`
                  : communityStats.away_percentage > communityStats.home_percentage
                    ? `${game.away_team?.abbreviation} ${communityStats.away_percentage}%`
                    : 'Split 50%'}
              </p>
            </div>
          </div>
          
          {/* Community prediction bar */}
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{game.away_team?.abbreviation}: {communityStats.away_percentage}%</span>
            <span>{game.home_team?.abbreviation}: {communityStats.home_percentage}%</span>
          </div>
          <div className="mt-1 h-2 bg-secondary rounded-full overflow-hidden flex">
            <div 
              className="bg-purple-400 transition-all"
              style={{ width: `${communityStats.away_percentage}%` }}
            />
            <div 
              className="bg-purple-600 transition-all"
              style={{ width: `${communityStats.home_percentage}%` }}
            />
          </div>
        </div>
      )}

      {/* User's Own Prediction Section - Only for authenticated users on scheduled games */}
      {isScheduled && isAuthenticated && (
        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-sm" data-testid="user-prediction-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-400" />
              <span className="text-xs font-medium text-blue-400 uppercase tracking-wide">
                Your Pick
              </span>
            </div>
            {userPrediction && (
              <span className="text-xs text-green-400">
                <CheckCircle2 className="w-3 h-3 inline mr-1" />
                {userPrediction.predicted_winner === 'home' ? game.home_team?.abbreviation : game.away_team?.abbreviation}
              </span>
            )}
          </div>
          
          {canPredict ? (
            <div className="flex gap-2">
              <button
                onClick={(e) => handleQuickPredict(e, 'away')}
                disabled={isPredicting}
                className={cn(
                  "flex-1 py-2 px-3 rounded-sm text-xs font-medium transition-all",
                  userPrediction?.predicted_winner === 'away'
                    ? "bg-blue-500 text-white"
                    : "bg-secondary hover:bg-blue-500/20 text-muted-foreground hover:text-blue-400"
                )}
                data-testid="predict-away-btn"
              >
                {isPredicting ? (
                  <Loader2 className="w-3 h-3 animate-spin mx-auto" />
                ) : (
                  <>
                    {game.away_team?.abbreviation}
                    {userPrediction?.predicted_winner === 'away' && <CheckCircle2 className="w-3 h-3 inline ml-1" />}
                  </>
                )}
              </button>
              <button
                onClick={(e) => handleQuickPredict(e, 'home')}
                disabled={isPredicting}
                className={cn(
                  "flex-1 py-2 px-3 rounded-sm text-xs font-medium transition-all",
                  userPrediction?.predicted_winner === 'home'
                    ? "bg-blue-500 text-white"
                    : "bg-secondary hover:bg-blue-500/20 text-muted-foreground hover:text-blue-400"
                )}
                data-testid="predict-home-btn"
              >
                {isPredicting ? (
                  <Loader2 className="w-3 h-3 animate-spin mx-auto" />
                ) : (
                  <>
                    {game.home_team?.abbreviation}
                    {userPrediction?.predicted_winner === 'home' && <CheckCircle2 className="w-3 h-3 inline ml-1" />}
                  </>
                )}
              </button>
            </div>
          ) : !isSubscriber ? (
            <p className="text-xs text-muted-foreground text-center">
              Subscribe to make predictions
            </p>
          ) : null}
        </div>
      )}

      {/* Footer - Local time display or live status */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
        {isLive ? (
          <div className="flex items-center gap-1 text-xs text-red-400">
            <Radio className="w-3 h-3 animate-pulse" />
            <span>Live • Updates every 30s</span>
          </div>
        ) : isScheduled ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>{formatMSTDate(game.start_time)} at {formatMSTTime(game.start_time)}</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-amber-500/80">
              <Lock className="w-2.5 h-2.5" />
              <span>Predictions lock 10min before start</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{formatMSTDate(game.start_time)} at {formatMSTTime(game.start_time)}</span>
          </div>
        )}
        {game.venue && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="w-3 h-3" />
            <span className="truncate max-w-[120px]">{game.venue}</span>
          </div>
        )}
      </div>
    </Link>
  );
};
